import React from 'react';
import { useForm } from 'react-hook-form';
import { CandidateRating, AssessmentPoint } from '../../types';
import Select from '../Form/Select';
import Button from '../Form/Button';

interface CandidateRatingFormProps {
  existingRatings: CandidateRating[];
  assessmentPoints: AssessmentPoint[];
  onSave: (ratings: CandidateRating[]) => void;
  onCancel: () => void;
  evaluatorName: string;
}

interface RatingFormData {
  [key: string]: number | string; // pointId -> score, pointId_training -> training note
}

const CandidateRatingForm: React.FC<CandidateRatingFormProps> = ({
  existingRatings,
  assessmentPoints,
  onSave,
  onCancel,
  evaluatorName
}) => {
  const availablePoints = assessmentPoints.filter(point => 
    point.isActive && (point.applicableFor === 'candidates' || point.applicableFor === 'both')
  );

  // Initialize form with existing ratings
  const initialData: RatingFormData = {};
  availablePoints.forEach(point => {
    const existingRating = existingRatings.find(r => r.pointId === point.id);
    initialData[point.id] = existingRating?.score || 0;
    initialData[`${point.id}_training`] = (existingRating as any)?.trainingNote || '';
  });

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting }
  } = useForm<RatingFormData>({
    defaultValues: initialData
  });

  const watchedValues = watch();

  // Calculate total weighted score
  const calculateTotalScore = () => {
    let totalWeightedScore = 0;
    let totalWeight = 0;
    
    availablePoints.forEach(point => {
      const score = Number(watchedValues[point.id]) || 0;
      if (score > 0) {
        totalWeightedScore += score * point.weight;
        totalWeight += point.weight;
      }
    });

    return totalWeight > 0 ? Math.round((totalWeightedScore / totalWeight) * 10) / 10 : 0;
  };

  const scoreOptions = [
    { value: '0', label: 'Not Rated' },
    { value: '1', label: '1 - Poor' },
    { value: '2', label: '2 - Below Average' },
    { value: '3', label: '3 - Fair' },
    { value: '4', label: '4 - Average' },
    { value: '5', label: '5 - Good' },
    { value: '6', label: '6 - Above Average' },
    { value: '7', label: '7 - Very Good' },
    { value: '8', label: '8 - Excellent' },
    { value: '9', label: '9 - Outstanding' },
    { value: '10', label: '10 - Exceptional' }
  ];

  const handleFormSubmit = (data: RatingFormData) => {
    const newRatings: CandidateRating[] = [];
    const timestamp = new Date().toISOString();

    availablePoints.forEach(point => {
      const score = Number(data[point.id]);
      const trainingNote = data[`${point.id}_training`] as string || '';
      if (score > 0) {
        newRatings.push({
          id: `rating_${point.id}_${Date.now()}`,
          pointId: point.id,
          pointName: point.name,
          score,
          evaluatedBy: evaluatorName,
          evaluatedAt: timestamp,
          candidate: '', // Will be set by parent
          assessmentPoint: point.id,
          trainingNote, // Training requirement note
          createdAt: timestamp,
          updatedAt: timestamp
        } as CandidateRating & { trainingNote: string });
      }
    });

    onSave(newRatings);
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'technical': return 'bg-blue-100 text-blue-800';
      case 'communication': return 'bg-green-100 text-green-800';
      case 'experience': return 'bg-purple-100 text-purple-800';
      case 'education': return 'bg-yellow-100 text-yellow-800';
      case 'leadership': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getWeightStars = (weight: number) => {
    return Array.from({ length: 5 }, (_, i) => (
      <svg
        key={i}
        className={`h-4 w-4 ${i < weight ? 'text-yellow-400' : 'text-gray-300'}`}
        fill="currentColor"
        viewBox="0 0 20 20"
      >
        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
      </svg>
    ));
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200 sticky top-0 bg-white z-10">
          <h2 className="text-xl font-semibold text-gray-900">
            Candidate Assessment Rating
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            Rate the candidate on each assessment point (1-10 scale). Leave as "Not Rated" if not applicable.
          </p>
        </div>
        
        <form onSubmit={handleSubmit(handleFormSubmit)} className="p-6">
          {/* Current Total Score Display */}
          <div className="mb-6 bg-blue-50 border border-blue-200 rounded-md p-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-sm font-medium text-blue-800">Current Total Score</h3>
                <p className="text-xs text-blue-600">Calculated based on weighted average of all ratings</p>
              </div>
              <div className="text-right">
                <div className="text-2xl font-bold text-blue-900">{calculateTotalScore()}</div>
                <div className="text-sm text-blue-700">out of 10</div>
              </div>
            </div>
          </div>

          {/* Assessment Points by Category */}
          <div className="space-y-6">
            {['technical', 'communication', 'experience', 'education', 'leadership', 'other'].map(category => {
              const categoryPoints = availablePoints.filter(point => point.category === category);
              
              if (categoryPoints.length === 0) return null;

              return (
                <div key={category} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center mb-4">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getCategoryColor(category)}`}>
                      {category.charAt(0).toUpperCase() + category.slice(1)}
                    </span>
                    <span className="ml-2 text-sm text-gray-500">
                      ({categoryPoints.length} point{categoryPoints.length !== 1 ? 's' : ''})
                    </span>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {categoryPoints.map(point => (
                      <div key={point.id} className="border border-gray-100 rounded-md p-3">
                        <div className="mb-2">
                          <div className="flex items-center justify-between">
                            <h4 className="text-sm font-medium text-gray-900">{point.name}</h4>
                            <div className="flex items-center">
                              {getWeightStars(point.weight)}
                            </div>
                          </div>
                          {point.description && (
                            <p className="text-xs text-gray-600 mt-1">{point.description}</p>
                          )}
                        </div>
                        
                        <Select
                          label=""
                          {...register(point.id, {
                            setValueAs: (value) => Number(value)
                          })}
                          options={scoreOptions}
                          error={errors[point.id]?.message}
                        />
                        
                        {watchedValues[point.id] > 0 && (
                          <div className="mt-2 text-xs text-gray-500">
                            Weighted Score: {(Number(watchedValues[point.id]) * point.weight).toFixed(1)}
                          </div>
                        )}

                        {/* Training Requirement Note */}
                        <div className="mt-3">
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Training Required (if needed)
                          </label>
                          <textarea
                            {...register(`${point.id}_training`)}
                            rows={2}
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 resize-none"
                            placeholder="If the candidate needs training on this skill, describe what training is required..."
                          />
                          <p className="mt-1 text-xs text-gray-400">
                            Leave empty if no training is needed for this skill
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Information Box */}
          <div className="mt-6 bg-yellow-50 border border-yellow-200 rounded-md p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-yellow-800">
                  Rating Guidelines
                </h3>
                <div className="mt-2 text-sm text-yellow-700">
                  <ul className="list-disc list-inside space-y-1">
                    <li>Each point is rated on a scale of 1-10 (1 = Poor, 10 = Exceptional)</li>
                    <li>Star ratings show the importance weight of each assessment point</li>
                    <li>Total score is calculated as a weighted average of all rated points</li>
                    <li>You can leave points as "Not Rated" if they don't apply to this candidate</li>
                    <li><strong>Training Note:</strong> Use the training field to note if the candidate needs specific training on that skill</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200 mt-6">
            <Button
              type="button"
              variant="secondary"
              onClick={onCancel}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={isSubmitting}
              loading={isSubmitting}
            >
              Save Rating
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CandidateRatingForm;
